import { db } from '../../services/firebase';
import {
    collection,
    addDoc,
    setDoc,
    updateDoc,
    deleteDoc,
    getDocs,
    getDoc,
    doc,
    query,
    orderBy,
    where,
    serverTimestamp,
    Timestamp,
    onSnapshot,
    limit,
    writeBatch,
} from 'firebase/firestore';
import { getUserWorkspace } from '../../services/firestore';
import { autoCreateZoomRoomsForCourse } from '../../services/zoomApiService';

// ── Multi-tenant workspace context ──
// Set workspace once, all queries filter by it automatically
let _currentWorkspaceId = null;

export const setCalendarWorkspace = async (userId) => {
    if (!userId) return;
    _currentWorkspaceId = await getUserWorkspace(userId);
    return _currentWorkspaceId;
};

export const getCalendarWorkspaceId = () => _currentWorkspaceId;

// Helper: add workspaceId to data before insert
const withWs = (data) => ({ ...data, workspaceId: _currentWorkspaceId || 'default' });

// Helper: filter by workspaceId
const wsWhere = () => _currentWorkspaceId ? where('workspaceId', '==', _currentWorkspaceId) : null;

// Build query with optional workspace filter
const wsQuery = (colRef, ...constraints) => {
    const ws = wsWhere();
    const allConstraints = ws ? [ws, ...constraints] : constraints;
    return query(colRef, ...allConstraints);
};

// --- INSTRUCTORS ---

const INSTRUCTORS_COLLECTION = 'instructors';

export const getInstructors = async () => {
    const q = wsQuery(collection(db, INSTRUCTORS_COLLECTION), orderBy('name'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const getInstructorById = async (id) => {
    const docRef = doc(db, INSTRUCTORS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
};

export const createInstructor = async (instructorData) => {
    // Enriquecer con campos nuevos del modelo avanzado
    const enrichedData = {
        ...instructorData,
        // Estado operativo
        estado: instructorData.estado || 'activo', // nuevo | activo | mixto | saturado | descanso
        // Límites
        maxCursos: instructorData.maxCursos || 4,
        maxEventosDia: instructorData.maxEventosDia || 3,
        // Tracking
        cursosActivos: instructorData.cursosActivos || [],
        masterclassActivas: instructorData.masterclassActivas || [],
        cargaActual: instructorData.cargaActual || 0,
        // Días bloqueados específicos
        diasBloqueados: instructorData.diasBloqueados || [], // [{fecha: '2026-02-10', motivo: 'Viaje'}]
        // Metadatos
        createdAt: serverTimestamp()
    };
    return await addDoc(collection(db, INSTRUCTORS_COLLECTION), withWs(enrichedData));
};

export const updateInstructor = async (id, instructorData) => {
    const ref = doc(db, INSTRUCTORS_COLLECTION, id);
    return await updateDoc(ref, {
        ...instructorData,
        updatedAt: serverTimestamp()
    });
};

export const deleteInstructor = async (id) => {
    return await deleteDoc(doc(db, INSTRUCTORS_COLLECTION, id));
};

// Calcular carga actual de un instructor
export const calculateInstructorLoad = async (instructorId) => {
    const schedules = await getSchedulesByInstructor(instructorId);
    const now = new Date();
    const activeEvents = schedules.filter(s => s.end >= now);

    // Contar cursos únicos activos
    const uniqueCourses = [...new Set(activeEvents.map(e => e.courseId))];

    return {
        eventosActivos: activeEvents.length,
        cursosActivos: uniqueCourses.length,
        cargaActual: uniqueCourses.length
    };
};

// Verificar si instructor está saturado
export const isInstructorSaturated = async (instructorId) => {
    const instructor = await getInstructorById(instructorId);
    if (!instructor) return false;

    const load = await calculateInstructorLoad(instructorId);
    return load.cursosActivos >= (instructor.maxCursos || 4);
};

// --- COURSES ---

const COURSES_COLLECTION = 'courses';

export const getCourses = async () => {
    const q = wsQuery(collection(db, COURSES_COLLECTION), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => {
        const data = d.data();
        return { id: d.id, ...data, name: data.name || data.title || 'Sin nombre' };
    });
};

// Lectura puntual de cursos por id (SIN filtro de workspace).
// Se usa en la vista de instructor: el instructor puede vivir en otro workspace
// que el de la academia, pero necesita los datos de SUS cursos (nombre, zoom, etc.).
export const getCoursesByIds = async (ids = []) => {
    const unique = [...new Set(ids.filter(Boolean))];
    const docs = await Promise.all(unique.map(id => getDoc(doc(db, COURSES_COLLECTION, id))));
    return docs
        .filter(s => s.exists())
        .map(s => {
            const data = s.data();
            return { id: s.id, ...data, name: data.name || data.title || 'Sin nombre' };
        });
};

// Cascade rename: actualiza el title de todos los eventos del curso
// (filtrado estricto por courseId). NO toca eventos de otros cursos.
//
// Estrategia 1: si el título contiene el nombre viejo, reemplaza con el nuevo (clean).
// Estrategia 2 (fallback): si el título no tiene el nombre viejo pero tiene un prefijo
//   conocido (Clase, CL, MC, 1M -, 2M -, SESION X -, Masterclass-), reemplaza el
//   resto con el nombre nuevo. Útil cuando los eventos quedaron desfasados desde
//   un rename anterior al fix.
export const cascadeRenameCourseEvents = async (courseId, oldName, newName) => {
    if (!courseId || !newName) return 0;
    const q = query(
        collection(db, SCHEDULES_COLLECTION),
        where('courseId', '==', courseId)
    );
    const snapshot = await getDocs(q);
    let updated = 0;

    const PREFIX_PATTERNS = [
        /^(\d+M\s*-\s*)/i,                          // "1M -", "2M -"
        /^(SESION\s+\d+(?:\s+FINAL)?\s*-\s*)/i,     // "SESION 1 -", "SESION 1 FINAL -"
        /^(Masterclass[-\s]+)/i,                    // "Masterclass-..." o "Masterclass ..."
        /^(MC\s+)/i,                                // "MC ..."
        /^(Clase\s+)/i,                             // "Clase ..."
        /^(CL\s+)/i,                                // "CL ..."
    ];

    await Promise.all(snapshot.docs.map(async (d) => {
        const data = d.data();
        const title = data.title || '';
        if (!title) return;

        // Estrategia 1: replace clean
        if (oldName && oldName !== newName && title.includes(oldName)) {
            const newTitle = title.split(oldName).join(newName);
            if (newTitle !== title) {
                await updateDoc(doc(db, SCHEDULES_COLLECTION, d.id), {
                    title: newTitle,
                    updatedAt: serverTimestamp(),
                });
                updated++;
                return;
            }
        }

        // Si ya tiene el nombre nuevo, no hace falta tocar
        if (title.includes(newName)) return;

        // Estrategia 2: detectar prefijo y reemplazar el resto
        for (const pattern of PREFIX_PATTERNS) {
            const match = title.match(pattern);
            if (match) {
                const prefix = match[1];
                const newTitle = prefix + newName;
                if (newTitle !== title) {
                    await updateDoc(doc(db, SCHEDULES_COLLECTION, d.id), {
                        title: newTitle,
                        updatedAt: serverTimestamp(),
                    });
                    updated++;
                }
                return;
            }
        }
    }));
    return updated;
};

// Real-time subscription para mantener sincronizado con Generador.
export const subscribeToCalendarCourses = (callback) => {
    const q = wsQuery(collection(db, COURSES_COLLECTION), orderBy('createdAt', 'desc'));
    return onSnapshot(q, (snapshot) => {
        const courses = snapshot.docs.map(d => {
            const data = d.data();
            return { id: d.id, ...data, name: data.name || data.title || 'Sin nombre' };
        });
        callback(courses);
    }, (error) => {
        console.error('Error subscribing to calendar courses:', error);
        callback([]);
    });
};

export const getCourseById = async (id) => {
    const docRef = doc(db, COURSES_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
};

export const createCourse = async (courseData) => {
    // Enriquecer con campos nuevos del modelo avanzado
    const enrichedData = {
        ...courseData,
        // Ensure both name and title exist for cross-module compatibility
        title: courseData.title || courseData.name || '',
        name: courseData.name || courseData.title || '',
        // Fase del curso
        fase: courseData.fase || 'pre_lanzamiento', // pre_lanzamiento | lanzamiento | activo | mixto | maduro
        // Modo operativo
        modo: courseData.modo || 'solo_masterclass', // solo_masterclass | mixto | solo_clases
        // Intensidad comercial
        intensidadComercial: courseData.intensidadComercial || 'media', // baja | media | alta | explosiva
        // Tracking de masterclass
        masterclassProgramadas: courseData.masterclassProgramadas || 0,
        masterclassRealizadas: courseData.masterclassRealizadas || 0,
        // Tracking de clases
        clasesProgramadas: courseData.clasesProgramadas || 0,
        clasesRealizadas: courseData.clasesRealizadas || 0,
        // Métricas comerciales
        rendimientoPublicidad: courseData.rendimientoPublicidad || 50, // 1-100
        leadsActivos: courseData.leadsActivos || 0,
        conversionRate: courseData.conversionRate || 0,
        // Grupos WhatsApp
        gruposWhatsapp: courseData.gruposWhatsapp || [], // [{nombre, link, capacidad}]
        // Metadatos
        createdAt: serverTimestamp()
    };
    const ref = await addDoc(collection(db, COURSES_COLLECTION), withWs(enrichedData));
    // Salas de Zoom (MC y Clase) del curso nuevo. Fire-and-forget: no bloquea ni rompe la creación.
    autoCreateZoomRoomsForCourse(_currentWorkspaceId, ref.id, enrichedData.name);
    return ref;
};

export const updateCourse = async (id, courseData) => {
    const ref = doc(db, COURSES_COLLECTION, id);
    return await updateDoc(ref, {
        ...courseData,
        updatedAt: serverTimestamp()
    });
};

export const deleteCourse = async (id) => {
    return await deleteDoc(doc(db, COURSES_COLLECTION, id));
};

// Calcular fase automática del curso basada en fechas
export const calculateCourseFase = (course) => {
    const now = new Date();
    const startDate = new Date(course.startDate);
    const masterphaseEnd = new Date(startDate);
    masterphaseEnd.setDate(masterphaseEnd.getDate() + (course.masterphaseDuration || 30));

    if (now < startDate) {
        return 'pre_lanzamiento';
    } else if (now <= masterphaseEnd && course.hasMasterclass) {
        return 'lanzamiento';
    } else if (course.hasRegular && course.hasMasterclass) {
        return 'mixto';
    } else if (course.hasRegular) {
        return 'activo';
    } else {
        return 'maduro';
    }
};

// --- MASTERCLASSES (Nueva entidad) ---

const MASTERCLASSES_COLLECTION = 'masterclasses';

export const getMasterclasses = async () => {
    const q = wsQuery(collection(db, MASTERCLASSES_COLLECTION), orderBy('fecha', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        fecha: doc.data().fecha?.toDate?.() || doc.data().fecha
    }));
};

export const getMasterclassesByCourse = async (courseId) => {
    const q = query(
        collection(db, MASTERCLASSES_COLLECTION),
        where('courseId', '==', courseId),
        orderBy('fecha', 'asc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        fecha: doc.data().fecha?.toDate?.() || doc.data().fecha
    }));
};

export const createMasterclass = async (masterclassData) => {
    const enrichedData = {
        ...masterclassData,
        tipo: 'venta',
        prioridad: masterclassData.prioridad || 'media', // baja | media | alta | urgente
        estado: masterclassData.estado || 'programada', // programada | confirmada | realizada | cancelada
        leadsEsperados: masterclassData.leadsEsperados || 0,
        leadsReales: masterclassData.leadsReales || 0,
        asistentes: masterclassData.asistentes || 0,
        conversion: masterclassData.conversion || 0,
        createdAt: serverTimestamp()
    };
    return await addDoc(collection(db, MASTERCLASSES_COLLECTION), withWs(enrichedData));
};

export const updateMasterclass = async (id, data) => {
    const ref = doc(db, MASTERCLASSES_COLLECTION, id);
    return await updateDoc(ref, {
        ...data,
        updatedAt: serverTimestamp()
    });
};

export const deleteMasterclass = async (id) => {
    return await deleteDoc(doc(db, MASTERCLASSES_COLLECTION, id));
};

// --- SCHEDULES (EVENTS) ---

const SCHEDULES_COLLECTION = 'schedules';

export const getSchedules = async () => {
    const q = wsQuery(collection(db, SCHEDULES_COLLECTION));
    const snapshot = await getDocs(q);
    return snapshot.docs
        // Clases de cursos desactivados quedan ocultas (hidden=true) pero guardadas
        .filter(doc => doc.data().hidden !== true)
        .map(doc => ({
            id: doc.id,
            ...doc.data(),
            start: doc.data().start?.toDate?.() || new Date(doc.data().start),
            end: doc.data().end?.toDate?.() || new Date(doc.data().end)
        }));
};

// Ocultar/mostrar TODAS las clases de un curso (soft-hide al activar/desactivar curso).
// hidden=true: salen del calendario pero quedan guardadas; hidden=false: reaparecen tal cual (mismo ID).
export const setCourseSchedulesHidden = async (courseId, hidden) => {
    if (!courseId) return 0;
    const q = query(collection(db, SCHEDULES_COLLECTION), where('courseId', '==', courseId));
    const snap = await getDocs(q);
    if (snap.empty) return 0;
    await commitHiddenInChunks(snap.docs.map(d => d.id), hidden);
    return snap.size;
};

// Firestore limita un batch a 500 escrituras: se parte en lotes.
const commitHiddenInChunks = async (ids, hidden) => {
    for (let i = 0; i < ids.length; i += 450) {
        const batch = writeBatch(db);
        ids.slice(i, i + 450).forEach(id => batch.update(doc(db, SCHEDULES_COLLECTION, id), { hidden: !!hidden }));
        await batch.commit();
    }
};

const toEventDates = (d) => ({
    id: d.id,
    ...d.data(),
    start: d.data().start?.toDate?.() || new Date(d.data().start),
    end: d.data().end?.toDate?.() || new Date(d.data().end),
});

// Grupo de un evento (los antiguos sin groupNumber cuentan como Grupo 1).
export const eventGroupOf = (ev) => Number(ev.groupNumber || ev.resource?.groupNumber) || 1;
export const isMasterclassEvent = (ev) => ev.type === 'masterclass';

// Todos los eventos del workspace, INCLUIDOS los ocultos (para contar visibles/ocultos).
export const getSchedulesIncludingHidden = async () => {
    const snapshot = await getDocs(wsQuery(collection(db, SCHEDULES_COLLECTION)));
    return snapshot.docs.map(toEventDates);
};

// Todos los eventos de un curso, incluidos los ocultos.
export const getCourseSchedulesAll = async (courseId) => {
    if (!courseId) return [];
    const snap = await getDocs(query(collection(db, SCHEDULES_COLLECTION), where('courseId', '==', courseId)));
    return snap.docs.map(toEventDates);
};

// Ocultar/mostrar solo una parte de los eventos de un curso.
//   group:    número de grupo, o null para todos los grupos
//   kind:     'mc' (masterclass), 'cl' (clases) o null para ambos
//   fromDate: solo eventos que empiezan desde esa fecha (null = todos)
// Devuelve cuántos eventos cambiaron.
export const setCourseSchedulesHiddenWhere = async (courseId, { group = null, kind = null, fromDate = null } = {}, hidden) => {
    const events = await getCourseSchedulesAll(courseId);
    const ids = events
        .filter(ev => group == null || eventGroupOf(ev) === group)
        .filter(ev => kind == null || (kind === 'mc') === isMasterclassEvent(ev))
        .filter(ev => !fromDate || ev.start >= fromDate)
        .filter(ev => (ev.hidden === true) !== !!hidden)
        .map(ev => ev.id);
    await commitHiddenInChunks(ids, hidden);
    return ids.length;
};

export const getSchedulesByInstructor = async (instructorId) => {
    const q = query(
        collection(db, SCHEDULES_COLLECTION),
        where('instructorId', '==', instructorId)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        start: doc.data().start?.toDate?.() || new Date(doc.data().start),
        end: doc.data().end?.toDate?.() || new Date(doc.data().end)
    }));
};

export const getSchedulesByDate = async (date) => {
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);

    const q = query(
        collection(db, SCHEDULES_COLLECTION),
        where('start', '>=', Timestamp.fromDate(startOfDay)),
        where('start', '<=', Timestamp.fromDate(endOfDay))
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        start: doc.data().start?.toDate?.() || new Date(doc.data().start),
        end: doc.data().end?.toDate?.() || new Date(doc.data().end)
    }));
};

export const createSchedule = async (eventData) => {
    return await addDoc(collection(db, SCHEDULES_COLLECTION), withWs({
        ...eventData,
        createdAt: serverTimestamp()
    }));
};

export const updateSchedule = async (id, eventData) => {
    const ref = doc(db, SCHEDULES_COLLECTION, id);
    return await updateDoc(ref, {
        ...eventData,
        updatedAt: serverTimestamp()
    });
};

export const deleteSchedule = async (id) => {
    return await deleteDoc(doc(db, SCHEDULES_COLLECTION, id));
};

// Batch create schedules (for AI generation)
export const saveGeneratedSchedule = async (events) => {
    const promises = events.map(event =>
        addDoc(collection(db, SCHEDULES_COLLECTION), withWs({
            ...event,
            createdAt: serverTimestamp()
        }))
    );
    return await Promise.all(promises);
};

// --- CAPACIDAD DEL SISTEMA ---

const CAPACITY_COLLECTION = 'system_capacity';

export const getSystemCapacity = async (date) => {
    const dateStr = typeof date === 'string' ? date : date.toISOString().split('T')[0];
    const q = query(
        collection(db, CAPACITY_COLLECTION),
        where('fecha', '==', dateStr)
    );
    const snapshot = await getDocs(q);
    if (snapshot.docs.length > 0) {
        return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
    }
    // Retornar configuración por defecto
    return {
        fecha: dateStr,
        maxMasterclass: 5,
        maxEventosTotales: 10,
        maxPorAsesor: 3,
        maxPorInstructor: {},
        eventosProgramados: 0,
        disponibilidadRestante: 10
    };
};

export const updateSystemCapacity = async (date, capacityData) => {
    const dateStr = typeof date === 'string' ? date : date.toISOString().split('T')[0];
    const existing = await getSystemCapacity(date);

    if (existing.id) {
        const ref = doc(db, CAPACITY_COLLECTION, existing.id);
        return await updateDoc(ref, {
            ...capacityData,
            updatedAt: serverTimestamp()
        });
    } else {
        return await addDoc(collection(db, CAPACITY_COLLECTION), withWs({
            fecha: dateStr,
            ...capacityData,
            createdAt: serverTimestamp()
        }));
    }
};

// Calcular capacidad disponible para un día
export const calculateDayCapacity = async (date) => {
    const schedules = await getSchedulesByDate(date);
    const capacity = await getSystemCapacity(date);

    const masterclassCount = schedules.filter(s => s.type === 'masterclass').length;
    const totalCount = schedules.length;

    // Contar por instructor
    const byInstructor = {};
    schedules.forEach(s => {
        byInstructor[s.instructorId] = (byInstructor[s.instructorId] || 0) + 1;
    });

    return {
        fecha: date,
        masterclassProgramadas: masterclassCount,
        eventosTotales: totalCount,
        disponibleMasterclass: capacity.maxMasterclass - masterclassCount,
        disponibleTotal: capacity.maxEventosTotales - totalCount,
        porInstructor: byInstructor,
        saturado: totalCount >= capacity.maxEventosTotales,
        alerta: totalCount >= capacity.maxEventosTotales * 0.8
    };
};

// --- CONFIGURACIÓN GLOBAL ---

const CONFIG_COLLECTION = 'calendar_config';

export const getCalendarConfig = async () => {
    const q = wsQuery(collection(db, CONFIG_COLLECTION));
    const snapshot = await getDocs(q);
    if (snapshot.docs.length > 0) {
        return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
    }
    // Configuración por defecto
    return {
        modoSistema: 'equilibrio', // expansion | estabilidad | equilibrio
        maxMasterclassDiario: 5,
        maxEventosDiario: 10,
        maxMasterclassPorAsesor: 3,
        alertaSaturacion: 80, // porcentaje
        horariosPreferidos: {
            masterclass: ['19:00', '20:00'],
            regular: ['09:00', '14:00', '19:00']
        }
    };
};

export const updateCalendarConfig = async (configData) => {
    const existing = await getCalendarConfig();

    if (existing.id) {
        const ref = doc(db, CONFIG_COLLECTION, existing.id);
        return await updateDoc(ref, {
            ...configData,
            updatedAt: serverTimestamp()
        });
    } else {
        return await addDoc(collection(db, CONFIG_COLLECTION), withWs({
            ...configData,
            createdAt: serverTimestamp()
        }));
    }
};

// --- SUSCRIPCIÓN .ICS (Google Calendar) ---
// El token es el secreto del enlace: el feed incluye enlaces y contraseñas de Zoom.
// La Cloud Function `calendarFeed` lo busca en calendar_config (todo el workspace)
// o en instructors (solo ese instructor).

export const CALENDAR_FEED_BASE = 'https://us-central1-gestor-de-respuestas-hingenia.cloudfunctions.net/calendarFeed';

const newFeedToken = () => {
    const bytes = new Uint8Array(24);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
};

export const calendarFeedUrl = (token) => `${CALENDAR_FEED_BASE}?k=${token}`;

// Devuelve el token del calendario general; lo crea si falta o si `regenerate`.
export const ensureWorkspaceFeedToken = async (regenerate = false) => {
    const config = await getCalendarConfig();
    if (config.icalToken && !regenerate) return config.icalToken;
    const icalToken = newFeedToken();
    await updateCalendarConfig({ icalToken });
    return icalToken;
};

// Igual, pero para el calendario de un instructor (se guarda en su documento).
// Se relee el documento: la lista en pantalla puede estar desactualizada y crear
// un token nuevo por error rompería el enlace que el instructor ya usa.
export const ensureInstructorFeedToken = async (instructorId, regenerate = false) => {
    const ref = doc(db, INSTRUCTORS_COLLECTION, instructorId);
    if (!regenerate) {
        const snap = await getDoc(ref);
        if (snap.exists() && snap.data().icalToken) return snap.data().icalToken;
    }
    const icalToken = newFeedToken();
    await updateDoc(ref, { icalToken });
    return icalToken;
};

// --- MOTOR DE REGLAS DE NEGOCIO ---

export const BUSINESS_RULES = {
    // Reglas de fase de curso
    CURSO_NUEVO_SOLO_MASTERCLASS: {
        id: 'R001',
        nombre: 'Curso nuevo = Solo Masterclass',
        condicion: (curso) => curso.fase === 'pre_lanzamiento' || curso.fase === 'lanzamiento',
        accion: 'modo = solo_masterclass',
        prioridad: 1
    },

    // Reglas de rendimiento
    RENDIMIENTO_ALTO_AUMENTAR_FRECUENCIA: {
        id: 'R002',
        nombre: 'Rendimiento alto = Aumentar frecuencia',
        condicion: (curso) => curso.rendimientoPublicidad > 80,
        accion: 'intensidadComercial = alta',
        prioridad: 2
    },

    RENDIMIENTO_EXPLOSIVO: {
        id: 'R003',
        nombre: 'Rendimiento explosivo = Máxima frecuencia',
        condicion: (curso) => curso.rendimientoPublicidad > 95 && curso.leadsActivos > 100,
        accion: 'intensidadComercial = explosiva',
        prioridad: 1
    },

    // Reglas de instructor
    INSTRUCTOR_SATURADO_BLOQUEAR: {
        id: 'R004',
        nombre: 'Instructor saturado = Bloquear',
        condicion: (instructor) => instructor.cargaActual >= instructor.maxCursos,
        accion: 'estado = saturado',
        prioridad: 1
    },

    // Reglas de capacidad
    CAPACIDAD_LLENA_STOP: {
        id: 'R005',
        nombre: 'Capacidad llena = Stop',
        condicion: (capacidad) => capacidad.disponibleTotal <= 0,
        accion: 'bloquear_programacion',
        prioridad: 1
    }
};

// Evaluar reglas para un curso
export const evaluateRulesForCourse = (curso) => {
    const aplicadas = [];
    const recomendaciones = [];

    // Evaluar fase
    const faseCalculada = calculateCourseFase(curso);
    if (faseCalculada !== curso.fase) {
        recomendaciones.push({
            tipo: 'fase',
            mensaje: `Actualizar fase de "${curso.fase}" a "${faseCalculada}"`,
            accion: { fase: faseCalculada }
        });
    }

    // Evaluar modo según fase
    if (faseCalculada === 'pre_lanzamiento' || faseCalculada === 'lanzamiento') {
        if (curso.modo !== 'solo_masterclass' && curso.hasMasterclass) {
            recomendaciones.push({
                tipo: 'modo',
                mensaje: 'Curso en fase inicial debería estar en modo "solo_masterclass"',
                accion: { modo: 'solo_masterclass' }
            });
        }
        aplicadas.push(BUSINESS_RULES.CURSO_NUEVO_SOLO_MASTERCLASS);
    }

    // Evaluar intensidad por rendimiento
    if (curso.rendimientoPublicidad > 95 && curso.leadsActivos > 100) {
        recomendaciones.push({
            tipo: 'intensidad',
            mensaje: 'Rendimiento excepcional - Recomendar intensidad EXPLOSIVA',
            accion: { intensidadComercial: 'explosiva' }
        });
        aplicadas.push(BUSINESS_RULES.RENDIMIENTO_EXPLOSIVO);
    } else if (curso.rendimientoPublicidad > 80) {
        recomendaciones.push({
            tipo: 'intensidad',
            mensaje: 'Buen rendimiento - Recomendar intensidad ALTA',
            accion: { intensidadComercial: 'alta' }
        });
        aplicadas.push(BUSINESS_RULES.RENDIMIENTO_ALTO_AUMENTAR_FRECUENCIA);
    }

    return { aplicadas, recomendaciones };
};

// Evaluar reglas para un instructor
export const evaluateRulesForInstructor = async (instructor) => {
    const aplicadas = [];
    const recomendaciones = [];

    // Calcular carga actual
    const load = await calculateInstructorLoad(instructor.id);

    // Verificar saturación
    if (load.cursosActivos >= (instructor.maxCursos || 4)) {
        recomendaciones.push({
            tipo: 'saturacion',
            mensaje: `Instructor saturado (${load.cursosActivos}/${instructor.maxCursos || 4} cursos)`,
            accion: { estado: 'saturado' },
            critico: true
        });
        aplicadas.push(BUSINESS_RULES.INSTRUCTOR_SATURADO_BLOQUEAR);
    } else if (load.cursosActivos >= (instructor.maxCursos || 4) - 1) {
        recomendaciones.push({
            tipo: 'alerta',
            mensaje: `Instructor casi saturado (${load.cursosActivos}/${instructor.maxCursos || 4} cursos)`,
            accion: null,
            critico: false
        });
    }

    return { aplicadas, recomendaciones, carga: load };
};

// Detectar conflictos en el calendario
export const detectConflicts = async (newEvent, existingEvents = null) => {
    const conflicts = [];

    // Obtener eventos existentes si no se proporcionan
    const events = existingEvents || await getSchedules();

    const newStart = new Date(newEvent.start);
    const newEnd = new Date(newEvent.end);

    for (const event of events) {
        if (event.id === newEvent.id) continue; // Skip self

        const eventStart = new Date(event.start);
        const eventEnd = new Date(event.end);

        // Verificar solapamiento de tiempo
        const timeOverlap = newStart < eventEnd && newEnd > eventStart;

        if (timeOverlap) {
            // Mismo instructor
            if (event.instructorId === newEvent.instructorId) {
                conflicts.push({
                    tipo: 'instructor_overlap',
                    mensaje: `Conflicto: Instructor ya tiene evento "${event.title}" en este horario`,
                    evento: event,
                    critico: true
                });
            }

            // Mismo curso (podría ser válido en algunos casos)
            if (event.courseId === newEvent.courseId && event.type === newEvent.type) {
                conflicts.push({
                    tipo: 'curso_duplicado',
                    mensaje: `Advertencia: Ya existe un evento del mismo tipo para este curso`,
                    evento: event,
                    critico: false
                });
            }
        }
    }

    return conflicts;
};

// Sugerir reubicación para un evento con conflicto
export const suggestRelocation = async (event, instructor) => {
    const suggestions = [];
    const eventDate = new Date(event.start);
    const eventDuration = (new Date(event.end) - new Date(event.start)) / (1000 * 60 * 60); // horas

    // Buscar huecos en los próximos 7 días
    for (let i = 0; i < 7; i++) {
        const checkDate = new Date(eventDate);
        checkDate.setDate(checkDate.getDate() + i);

        const dayOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][checkDate.getDay()];

        // Verificar disponibilidad del instructor ese día
        if (instructor.availability && instructor.availability[dayOfWeek]) {
            const slots = instructor.availability[dayOfWeek];

            for (const slot of slots) {
                const [startTime, endTime] = slot.split('-');
                const [startHour, startMin] = startTime.split(':').map(Number);
                const [endHour, endMin] = endTime.split(':').map(Number);

                const slotDuration = (endHour + endMin / 60) - (startHour + startMin / 60);

                if (slotDuration >= eventDuration) {
                    const suggestedStart = new Date(checkDate);
                    suggestedStart.setHours(startHour, startMin, 0, 0);

                    const suggestedEnd = new Date(suggestedStart);
                    suggestedEnd.setHours(suggestedStart.getHours() + eventDuration);

                    // Verificar que no haya conflicto
                    const testEvent = { ...event, start: suggestedStart, end: suggestedEnd };
                    const conflicts = await detectConflicts(testEvent);

                    if (conflicts.filter(c => c.critico).length === 0) {
                        suggestions.push({
                            fecha: checkDate.toISOString().split('T')[0],
                            hora: startTime,
                            start: suggestedStart,
                            end: suggestedEnd,
                            razon: i === 0 ? 'Mismo día, horario alternativo' : `${i} día(s) después`
                        });
                    }
                }
            }
        }
    }

    return suggestions.slice(0, 5); // Máximo 5 sugerencias
};

// --- ASESORES (SALES ADVISORS) ---

const ASESORES_COLLECTION = 'asesores';

export const getAsesores = async () => {
    const q = query(collection(db, 'users'), where('isAsesor', '==', true));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const getAsesorById = async (id) => {
    const docRef = doc(db, ASESORES_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
};

export const createAsesor = async (asesorData) => {
    const enrichedData = {
        ...asesorData,
        // Capacidad diaria de masterclasses
        maxMcPerDay: asesorData.maxMcPerDay ?? 2,
        // Franjas horarias disponibles (array de strings '18:00', '19:00', etc.)
        availableSlots: asesorData.availableSlots || ['18:00', '19:00'],
        // Estado del asesor
        estado: asesorData.estado || 'activo',
        // Metadatos
        createdAt: serverTimestamp()
    };
    return await addDoc(collection(db, ASESORES_COLLECTION), withWs(enrichedData));
};

export const updateAsesor = async (id, asesorData) => {
    const ref = doc(db, ASESORES_COLLECTION, id);
    return await updateDoc(ref, {
        ...asesorData,
        updatedAt: serverTimestamp()
    });
};

export const deleteAsesor = async (id) => {
    return await deleteDoc(doc(db, ASESORES_COLLECTION, id));
};

// Calcular masterclass requeridas basado en asesores activos
export const calculateRequiredMasterclass = async (courseId = null) => {
    const asesores = await getAsesores();
    const asesoresActivos = asesores.filter(a => a.estado === 'activo');

    if (courseId) {
        // Para un curso específico
        const asesoresCurso = asesoresActivos.filter(a =>
            !a.cursosAsignados || a.cursosAsignados.length === 0 || a.cursosAsignados.includes(courseId)
        );
        const totalCuota = asesoresCurso.reduce((sum, a) => sum + (a.cuotaMasterclass || 2), 0);
        return {
            asesores: asesoresCurso.length,
            masterclassRequeridas: totalCuota,
            detalle: asesoresCurso.map(a => ({ id: a.id, name: a.name, cuota: a.cuotaMasterclass || 2 }))
        };
    } else {
        // Global
        const totalCuota = asesoresActivos.reduce((sum, a) => sum + (a.cuotaMasterclass || 2), 0);
        return {
            asesores: asesoresActivos.length,
            masterclassRequeridas: totalCuota,
            detalle: asesoresActivos.map(a => ({ id: a.id, name: a.name, cuota: a.cuotaMasterclass || 2 }))
        };
    }
};

// --- BORRADORES DE PROGRAMACIÓN EN LA NUBE (cal_drafts) ---

const CAL_DRAFTS_COLLECTION = 'cal_drafts';
const MAX_DRAFTS = 40;

/**
 * Suscripción en tiempo real a todos los borradores.
 * @param {(drafts: object[]) => void} callback
 * @returns {() => void} unsubscribe function
 */
// ── Auto-save drafts per course+instructor ──

export const getDraftsRealtime = (callback) => {
    const q = wsQuery(collection(db, CAL_DRAFTS_COLLECTION), orderBy('updatedAt', 'desc'), limit(50));
    return onSnapshot(q, (snapshot) => {
        callback(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    });
};

// Get draft ID for a course+instructor combo
const getDraftKey = (courseId, instructorId) => `${courseId || 'none'}_${instructorId || 'none'}`;

// Auto-save: upsert draft by course+instructor (1 per combo)
export const autoSaveDraft = async (courseId, instructorId, rows, user, courses = [], instructors = []) => {
    const key = getDraftKey(courseId, instructorId);
    const docRef = doc(db, CAL_DRAFTS_COLLECTION, key);
    const courseName = courses.find(c => c.id === courseId)?.name || 'Sin curso';
    const instName = instructors.find(i => i.id === instructorId)?.name || 'Sin instructor';

    // Get existing doc for history
    const existing = await getDoc(docRef);
    let history = [];
    if (existing.exists()) {
        const prev = existing.data();
        history = prev.history || [];
        // Add current version to history (max 10)
        if (prev.rows && prev.rows.length > 0) {
            history.unshift({
                rows: prev.rows,
                editedBy: prev.lastEditedBy || {},
                editedAt: prev.updatedAt || null,
                rowCount: (prev.rows || []).filter(r => r.date).length,
            });
            if (history.length > 10) history = history.slice(0, 10);
        }
    }

    await setDoc(docRef, withWs({
        courseId: courseId || '',
        instructorId: instructorId || '',
        courseName,
        instructorName: instName,
        rows,
        rowCount: rows.filter(r => r.date).length,
        lastEditedBy: {
            uid: user?.uid || '',
            displayName: user?.displayName || user?.email || '',
            photoURL: user?.photoURL || null,
        },
        updatedAt: serverTimestamp(),
        history,
    }));
};

// Load draft for specific course+instructor
export const loadDraftForCourse = async (courseId, instructorId) => {
    const key = getDraftKey(courseId, instructorId);
    const snap = await getDoc(doc(db, CAL_DRAFTS_COLLECTION, key));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

// Delete draft
export const deleteDraft = async (courseId, instructorId) => {
    const key = getDraftKey(courseId, instructorId);
    await deleteDoc(doc(db, CAL_DRAFTS_COLLECTION, key));
};

// Legacy delete by ID
export const deleteDraftById = async (id) => {
    await deleteDoc(doc(db, CAL_DRAFTS_COLLECTION, id));
};

// Backward compat
export const saveDraft = autoSaveDraft;
